export const CREATOR_ANYONE = 'anyone';
export const CREATOR_ME = 'me';

/**
 * Pure creator-filter over one already-fetched page of video questions.
 * Name search and paging moved server-side (the `term`/`page` query params on
 * the `video_library_questions_data` accessor) - this is what is left once
 * that machinery does the rest, and it stays testable with plain arrays.
 *
 * Page-local, not exhaustive: it only sees the items already fetched, so on
 * a second page it filters that page, not the whole library. Server-side
 * creator filtering would need a matching accessor parameter, which is not
 * built yet.
 */
export function filterVideoQuestions(items, { creatorFilter, myVideoIds } = {}) {
    if (creatorFilter !== CREATOR_ME || !myVideoIds) return items;
    return items.filter((item) => myVideoIds.has(item.id));
}

/**
 * True when the view is narrowed by something the viewer can still clear.
 */
export function hasActiveFilter({ term, creatorFilter } = {}) {
    return Boolean((term || '').trim()) || (creatorFilter !== undefined && creatorFilter !== CREATOR_ANYONE);
}

/**
 * Whether the filter bar belongs on screen.
 *
 * It has to outlive an empty result, or the search box goes away with the results
 * it emptied and the term it still holds becomes unclearable. Three things decide it,
 * and each one is a way that has already gone wrong:
 *
 * - `fetched`: before the first response there is nothing to say, and a bar over a
 *   list that has not loaded shows "no match" on a tab the viewer just opened.
 * - `typedTerm` AND `appliedTerm`: `term` is applied server-side, so between the last
 *   keystroke and its response the typed term and the shown results disagree. Clearing
 *   a search that matched nothing would otherwise drop the bar for that gap and the
 *   input would come back without focus.
 * - `creatorFilter`: pass CREATOR_ANYONE where the control is not shown, or a stale
 *   choice from another tab counts as narrowing a tab that never applied it.
 */
export function shouldShowFilterBar({ fetched, resultCount, typedTerm, appliedTerm, creatorFilter } = {}) {
    if (!fetched) return false;
    if (resultCount) return true;
    return hasActiveFilter({ term: typedTerm, creatorFilter })
        || hasActiveFilter({ term: appliedTerm, creatorFilter });
}
